import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip, Stack } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteGenericRQScoringConfig,
  reactivateGenericRQScoringConfig,
} from "../../services/scoringService";
import { getFormattedDateTime } from "../../services/utils";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedGenericRQScoringConfigs from "../../hooks/useFetchPaginatedGenericRQScoringConfigs";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ButtonComponent from "../../components/Button.Component";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";

const FILTER_CONFIG = {
  DEFAULT_STATE: { generic_research_query_id: "", status_filter: "Active" },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: ["generic_research_query_id"] as string[],
    STRING_FIELDS: ["status_filter"],
    DATE_FIELDS: [] as never[],
  },
} as const;

const GenericRQScoringList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit =
    isAdmin || hasPermission("research_query_scoring_config", "edit");
  const canAdd =
    isAdmin || hasPermission("research_query_scoring_config", "add");
  const canDelete =
    isAdmin || hasPermission("research_query_scoring_config", "delete");
  const { rowsPerPage } = useRowsPerPage();
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const parseUrlFilter = (field: string, raw: string): any => {
    if (FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(field)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : "";
    }
    return raw;
  };

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const s: any = { ...FILTER_CONFIG.DEFAULT_STATE };
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = parseUrlFilter(f, v);
    });
    return s;
  });

  const [fetchedData, refetch] = useFetchPaginatedGenericRQScoringConfigs({
    setIsLoading,
    page,
    pSize,
    searchFilterData,
  });

  useEffect(() => {
    const p = new URLSearchParams();
    // Preserve ScoringConfig's reserved tab params so filter writes don't
    // wipe parent tab state from the URL.
    ["tab", "runSub", "aggSub"].forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null) p.set(k, v);
    });
    FILTER_CONFIG.URL_FIELDS.forEach((f) => {
      const v = searchFilterData[f];
      if (v) p.set(f, String(v));
    });
    if (searchParams.toString() !== p.toString())
      setSearchParams(p, { replace: true });
  }, [searchFilterData]);

  useEffect(() => {
    const s: any = {};
    let changed = false;
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const urlVal = searchParams.get(f);
      if (urlVal) {
        s[f] = parseUrlFilter(f, urlVal);
      } else if (f === "status_filter") {
        s[f] = FILTER_CONFIG.DEFAULT_STATE.status_filter;
      } else {
        s[f] =
          searchFilterData[f] ??
          FILTER_CONFIG.DEFAULT_STATE[
            f as keyof typeof FILTER_CONFIG.DEFAULT_STATE
          ];
      }
    });
    changed = Object.keys(s).some((k) => s[k] !== searchFilterData[k]);
    if (changed) setSearchFilterData(s);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);
  useEffect(() => {
    if (fetchedData?.items) setFileData(fetchedData);
  }, [fetchedData]);

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setDeleteConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteGenericRQScoringConfig(itemToDelete.id);
      showToast(`Config #${itemToDelete.id} deleted`, "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to delete config"),
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReactivate = async (row: any) => {
    setIsLoading(true);
    try {
      await reactivateGenericRQScoringConfig(row.id);
      showToast(`Config #${row.id} reactivated`, "success");
      refetch();
    } catch {
      showToast("Error reactivating config", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: () => <></>,
    },
    { field: "id", headerName: "ID", minWidth: 70, flex: 0.5 },
    {
      field: "generic_research_query_id",
      headerName: "Query ID",
      minWidth: 100,
      flex: 0.7,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={String(p.value ?? "")} />
      ),
    },
    {
      field: "research_query_name",
      headerName: "Query Name",
      minWidth: 160,
      flex: 1.2,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p.value ?? ""} />
      ),
    },
    {
      field: "impact_of_record_level",
      headerName: "Record Level",
      minWidth: 110,
      flex: 0.8,
    },
    {
      field: "impact_of_issue_level",
      headerName: "Issue Level",
      minWidth: 110,
      flex: 0.8,
    },
    { field: "factor_low", headerName: "Factor Low", minWidth: 90, flex: 0.6 },
    {
      field: "factor_medium",
      headerName: "Factor Med",
      minWidth: 90,
      flex: 0.6,
    },
    {
      field: "factor_high",
      headerName: "Factor High",
      minWidth: 90,
      flex: 0.6,
    },
    {
      field: "modified_by_user",
      headerName: "Modified By",
      minWidth: 130,
      flex: 1,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p?.value?.username} />
      ),
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 160,
      flex: 1,
      renderCell: (p: any) => getFormattedDateTime(p.value),
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 80,
      flex: 0.6,
      renderCell: (p: any) =>
        p.value ? (
          <Box title="Active">
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title="Inactive">
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "right",
      headerAlign: "right",
      width: 140,
      renderCell: (p: any) => {
        const isActive = p?.row?.is_active === true;
        return (
          <>
            {isActive && (
              <>
                <Tooltip title="View Config" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      navigate(`/scoring/generic-rq/view/${p.row.id}`)
                    }
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit Config" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/scoring/generic-rq/edit/${p.row.id}`)
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    <IconButton
                      key="audit"
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/scoring/generic-rq/audit/${p.row.generic_research_query_id}`
                        )
                      }
                    >
                      <IconAudit /> Audit History
                    </IconButton>,
                    ...(canDelete
                      ? [
                          <IconButton
                            key="delete"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete(p.row)}
                          >
                            <IconDeleteBlueSvg /> Delete Config
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}
            {!isActive && canEdit && (
              <Tooltip title="Reactivate Config" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(p.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      {(isLoading || deleteLoading) && (
        <Loader isLoading={isLoading || deleteLoading} />
      )}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Generic RQ Scoring Config"
        dailogDescription={`Are you sure you want to delete config #${itemToDelete?.id}?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2} alignItems="center">
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["generic_research_query_id", "status_filter"]}
              primaryFields={["generic_research_query_id", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
              }}
            />
          </Grid>
          {canAdd && (
            <Grid item display={"flex"} justifyContent={"flex-end"}>
              <Stack>
                <Box sx={{ display: "flex", columnGap: 2 }}>
                  <ButtonComponent
                    handelClickEvent={() => navigate("/scoring/generic-rq/add")}
                    className="btn-orange"
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} /> Generic RQ
                    Scoring
                  </ButtonComponent>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle="Generic RQ Scoring Config List"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default GenericRQScoringList;
